import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, User, LogIn } from "lucide-react";
import { useRegion } from "../../context/RegionContext";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Insights", to: "/insights" },
  { label: "Podcast", to: "/podcast" },
  { label: "Practitioners", to: "/practitioners" },
  { label: "Shop", to: "/shop" },
];

export default function Header() {
  const { region, setRegion, config } = useRegion();
  const { user, status } = useAuth();
  const [open, setOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const navigate = useNavigate();

  const isAuthed = status === "authed";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/70"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src="/brand-logo.webp" alt="All Things Social Impact" className="h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors ${isActive ? "text-brand-orange" : "text-slate-700 hover:text-brand-blue"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
            <button
              data-testid="region-switcher-button"
              onClick={() => setRegionOpen((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 hover:text-brand-blue px-3 py-2 border border-slate-200 rounded-sm"
            >
              <span>{config.flag}</span>
              <span>{region}</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {regionOpen && (
              <div data-testid="region-dropdown" className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 shadow-lg rounded-sm">
                {["AU", "US"].map((r) => (
                  <button
                    key={r}
                    data-testid={`region-option-${r}`}
                    onClick={() => { setRegion(r); setRegionOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 flex items-center justify-between ${region === r ? "text-brand-orange font-semibold" : "text-slate-700"}`}
                  >
                    <span>{r === "AU" ? "🇦🇺 Australia" : "🇺🇸 United States"}</span>
                    {region === r && <span className="text-[10px] uppercase tracking-widest">Active</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAuthed ? (
            <>
              {user?.role === "admin" && (
                <>
                  <button
                    data-testid="header-admin-orders-btn"
                    onClick={() => navigate("/admin/orders")}
                    className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange hover:text-brand-blue px-3 py-2 border border-brand-orange/40 rounded-sm"
                    title="Admin orders"
                  >
                    Orders
                  </button>
                  <button
                    data-testid="header-admin-members-btn"
                    onClick={() => navigate("/admin/members")}
                    className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange hover:text-brand-blue px-3 py-2 border border-brand-orange/40 rounded-sm"
                    title="Members directory"
                  >
                    Members
                  </button>
                  <button
                    data-testid="header-admin-curriculum-btn"
                    onClick={() => navigate("/admin/curriculum")}
                    className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange hover:text-brand-blue px-3 py-2 border border-brand-orange/40 rounded-sm"
                    title="Curriculum preview"
                  >
                    Curriculum
                  </button>
                  <button
                    data-testid="header-admin-insights-btn"
                    onClick={() => navigate("/admin/insights")}
                    className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange hover:text-brand-blue px-3 py-2 border border-brand-orange/40 rounded-sm"
                    title="Insights CMS"
                  >
                    Insights
                  </button>
                </>
              )}
              <button
                data-testid="header-member-hub-btn"
                onClick={() => navigate("/member/hub")}
                className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue hover:text-brand-orange px-3 py-2 border border-slate-200 rounded-sm"
                title={user?.email}
              >
                <User className="w-3.5 h-3.5" /> {user?.name?.split(" ")[0] || "Member"}
              </button>
            </>
          ) : (
            <button
              data-testid="header-signin-btn"
              onClick={() => navigate("/login")}
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 hover:text-brand-orange px-3 py-2 border border-slate-200 rounded-sm"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </button>
          )}

          <button
            data-testid="header-discuss-project-btn"
            onClick={() => navigate("/contact")}
            className="btn-primary hidden sm:inline-flex text-sm"
          >
            Contact Us
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 px-6 py-4">
          <nav className="flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `text-base font-semibold ${isActive ? "text-brand-orange" : "text-slate-800"}`}
              >
                {item.label}
              </NavLink>
            ))}
            {isAuthed ? (
              <NavLink to="/member/hub" onClick={() => setOpen(false)} data-testid="mobile-member-hub" className="text-base font-semibold text-brand-blue">
                Member Hub →
              </NavLink>
            ) : (
              <NavLink to="/login" onClick={() => setOpen(false)} data-testid="mobile-signin" className="text-base font-semibold text-brand-blue">
                Sign in
              </NavLink>
            )}
            {isAuthed && user?.role === "admin" && (
              <>
                <NavLink to="/admin/orders" onClick={() => setOpen(false)} data-testid="mobile-admin-orders" className="text-base font-semibold text-brand-orange">
                  Orders →
                </NavLink>
                <NavLink to="/admin/members" onClick={() => setOpen(false)} data-testid="mobile-admin-members" className="text-base font-semibold text-brand-orange">
                  Members →
                </NavLink>
                <NavLink to="/admin/curriculum" onClick={() => setOpen(false)} data-testid="mobile-admin-curriculum" className="text-base font-semibold text-brand-orange">
                  Curriculum →
                </NavLink>
                <NavLink to="/admin/insights" onClick={() => setOpen(false)} data-testid="mobile-admin-insights" className="text-base font-semibold text-brand-orange">
                  Insights CMS →
                </NavLink>
              </>
            )}
            <div className="pt-3 flex gap-3">
              {["AU", "US"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest border ${region === r ? "border-brand-orange text-brand-orange" : "border-slate-300 text-slate-600"}`}
                >
                  {r === "AU" ? "🇦🇺 Australia" : "🇺🇸 United States"}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/contact"); }}
              className="btn-primary mt-2"
            >
              Contact Us
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
